import React, { forwardRef } from 'react';
import IconButton from '@mui/material/IconButton';
import { alpha } from '@mui/material/styles';

import { useSalonPublicTheme } from '../../../context/SalonPublicThemeContext';
import { resolveSalonButtonColor } from './SalonPublicButton';


const asSxArray = (sx) => {
  if (!sx) return [];
  return Array.isArray(sx) ? sx : [sx];
};

const appearanceStyles = (appearance, color) => (muiTheme) => {
  const palette = muiTheme.palette[color];

  if (appearance === 'filled') {
    return {
      backgroundColor: palette.main,
      color: palette.contrastText,
      '&:hover': { backgroundColor: palette.dark },
    };
  }
  if (appearance === 'outlined') {
    return {
      border: `1px solid ${palette.main}`,
      color: palette.main,
      '&:hover': { backgroundColor: alpha(palette.main, 0.08) },
    };
  }
  return {
    backgroundColor: alpha(palette.main, 0.12),
    color: palette.main,
    '&:hover': { backgroundColor: alpha(palette.main, 0.2) },
  };
};

const SalonPublicIconButton = forwardRef(({
  tone = 'primary',
  style,
  sx,
  ...props
}, ref) => {
  const { layout, tokens: theme } = useSalonPublicTheme();
  const buttonTokens = layout.tokens.buttons;
  const color = resolveSalonButtonColor(tone);
  const hasAccessibleName = Boolean(
    props['aria-label'] || props['aria-labelledby'] || props.title,
  );

  if (!hasAccessibleName) {
    throw new Error(
      'SalonPublicIconButton requires aria-label, aria-labelledby, or title.',
    );
  }

  return (
    <IconButton
      {...props}
      ref={ref}
      color={color}
      data-salon-button-appearance={buttonTokens.appearance}
      data-salon-button-shape={buttonTokens.shape}
      data-salon-button-size={buttonTokens.size}
      data-salon-button-tone={tone}
      style={{
        ...style,
        borderRadius: buttonTokens.borderRadius,
        minHeight: buttonTokens.iconSize,
        minWidth: buttonTokens.iconSize,
        height: buttonTokens.iconSize,
        width: buttonTokens.iconSize,
      }}
      sx={[
        appearanceStyles(buttonTokens.appearance, color),
        {
          '&.Mui-focusVisible': {
            outline: `3px solid ${theme.salon.focus}`,
            outlineOffset: '2px',
          },
        },
        ...asSxArray(sx),
      ]}
    />
  );
});

SalonPublicIconButton.displayName = 'SalonPublicIconButton';

export default SalonPublicIconButton;
